import { zId, zSidLabel } from "src/types";
import z from "zod";

import { zOptionsMaker, zValueCatalogRef } from "../ZOptionsMaker";

const zAttrInput = z.object({
  type: z.literal("Input"),
  addonAfter: z.string().nullish(),
  addonBefore: z.string().nullish(),
});
export type ZAttrInput = z.infer<typeof zAttrInput>;

const zAttrInputNumber = z.object({
  type: z.literal("InputNumber"),
  addonAfter: z.string().nullish(),
  addonBefore: z.string().nullish(),
});
export type ZAttrInputNumber = z.infer<typeof zAttrInputNumber>;

const zAttrTextArea = z.object({
  type: z.literal("TextArea"),
  rows: z
    .number()
    .int()
    .min(1)
    .nullish()
    .describe("Количество текстовых строк"),
});
export type ZAttrTextArea = z.infer<typeof zAttrTextArea>;

export const zSelectSearchMode = z.enum(["start", "part"]);
export type ZSelectSearchMode = z.infer<typeof zSelectSearchMode>;

const zAttrSelect = z.object({
  type: z.literal("Select"),
  options: zOptionsMaker,
  search: zSelectSearchMode.nullish().describe("Режим поиска"),
});
export type ZAttrSelect = z.infer<typeof zAttrSelect>;

const zAttrDatePicker = z.object({
  type: z.literal("DatePicker"),
  prefix: z.string().nullish().describe("Префикс"),
  suffix: z.string().nullish().describe("Суффикс"),
  placement: z
    .enum(["bottomLeft", "bottomRight", "topLeft", "topRight"])
    .optional(),
});
export type ZAttrDatePicker = z.infer<typeof zAttrDatePicker>;

const zAttrSwitch = z.object({
  type: z.literal("Switch"),
  checkedText: z.string().nullish().describe("Текст для вкюченного состояния"),
  uncheckedText: z
    .string()
    .nullish()
    .describe("Текст для выключенного состояния"),
});
export type ZAttrSwitch = z.infer<typeof zAttrSwitch>;

const zAttrParentsSelect = z.object({
  type: z.literal("ParentsSelect"),
  classId: zId.nullish().describe("ID класса для загрузки доступных родителей"),
  parentId: zId
    .nullish()
    .describe("ID родителя для загрузки доступных родителей"),
  placeholder: z.string().nullish().describe("Текст подсказки"),
  drawerTitle: z.string().nullish().describe("Заголовок модального окна"),
  options: zOptionsMaker,
});
export type ZAttrParentsSelect = z.infer<typeof zAttrParentsSelect>;

const zAttrClassesSelect = z.object({
  type: z.literal("ClassesSelect"),
  classifier: zSidLabel,
  // данные о родителх используются для построения лейблов с инфо об иерархии типа "parent1/parent2/label"
  parentsMap: z.record(z.string(), zSidLabel.array().array()),
  labelTemplate: z
    .string()
    .nullish()
    .describe(
      "Шаблон для формирования наименований. Используются поля записей для указанного классификатора",
    ),
});
export type ZAttrClassesSelect = z.infer<typeof zAttrClassesSelect>;

const zAttrExternalImages = z.object({
  type: z.literal("AttrExternalImages"),
});

export const rxAccept =
  /^\s*((\.[^,]+)|([a-z]+\/[^,]+))\s*(,\s*((\.[^\s,]+)|([a-z]+\/[^,]+))\s*)*$/;

const zAttrUploader = z.object({
  type: z.literal("Uploader"),
  isImage: z.boolean(),
  maxSize: z.number().optional().describe("Максимальный размер в МБ"),
  accept: z
    .string()
    .regex(rxAccept, {
      message:
        "Требуется формат элемента: .ext или type/subtype. Элементы перечисляются через запятую",
    })
    .nullish()
    .describe(
      "Стандартный html-атрибут accept. Пример: '.doc,.docx,application/msword'",
    ),
});
export type ZAttrUploader = z.infer<typeof zAttrUploader>;

/** Выбор записей каталога */
const zAttrRecordSelect = z
  .object({
    type: z.literal("RecordSelect"),
    catalogId: z
      .uuid()
      .describe("Id каталога, в котором выбирается запись(записи)"),
    labelTemplate: z
      .string()
      .nullish()
      .describe("Шаблон для наименования запись. По-умолчанию: {{name}}"),
  })
  .describe("Поддерживаются типы: strOption и array[strOption]");
export type ZAttrRecordSelect = z.infer<typeof zAttrRecordSelect>;

const zSelectValuesModal = z
  .object({
    type: z.literal("SelectValuesModal"),
    valuesCatalogId: zValueCatalogRef,
    labelTemplate: z.string().describe("Шаблон для формирования наименований"),
  })
  .describe("Выбор из каталога значений в модальном окне");
export type ZSelectValuesModal = z.infer<typeof zSelectValuesModal>;

const zPrimarySpec = z.object({ type: z.literal("PrimarySpec") });

const zSpecsSelectTable = z.object({
  type: z.literal("SpecsSelectTable"),
  classId: z.string(),
  prohibitedNames: z.set(z.string()),
});
export type ZSpecsSelectTable = z.infer<typeof zSpecsSelectTable>;

export const zAttrComponent = z.discriminatedUnion("type", [
  zAttrInput,
  zAttrTextArea,
  zAttrSelect,
  zAttrInputNumber,
  zAttrDatePicker,
  zAttrSwitch,
  zAttrParentsSelect,
  zAttrClassesSelect,
  zAttrRecordSelect,
  zSelectValuesModal,
  zAttrExternalImages,
  zAttrUploader,
  zPrimarySpec,
  zSpecsSelectTable,
]);

export type ZAttrComponent = z.infer<typeof zAttrComponent>;
